import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ContactModel } from '../../models/contact.model';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-merge-contact-modal',
  standalone: false,
  templateUrl: './merge-contact-modal.component.html',
  styleUrls: ['./merge-contact-modal.component.scss'],
  providers: [DatePipe]
})
export class MergeContactModalComponent implements OnInit {
  @Input() mergedContact: Partial<ContactModel> = {};
  @Input() conflictingFields: Partial<Record<keyof ContactModel, string[]>> = {};
  @Input() visible = false;
  @Output() merged = new EventEmitter<Partial<ContactModel>>();
  @Output() canceled = new EventEmitter<void>();

  dropdownOptions: Partial<Record<keyof ContactModel, { label: string; value: any }[]>> = {};
  touchedFields = new Set<keyof ContactModel>();

  allFields: (keyof ContactModel)[] = [
    'gender', 'idCardNumber', 'firstName', 'lastName',
    'companyName', 'street', 'zipCode', 'city',
    'mobilePhoneNumber', 'mobilePhoneNumberName',
    'mobilePhoneNumber2', 'mobilePhoneNumber2Name',
    'phoneNumber', 'email', 'birthday', 'birthPlace'
  ];

  fieldColSpanMap: Record<string, number> = {
    gender: 5,
    idCardNumber: 7,
    firstName: 6,
    lastName: 6,
    companyName: 12,
    street: 6,
    zipCode: 6,
    city: 12,
    mobilePhoneNumber: 6,
    mobilePhoneNumberName: 6,
    mobilePhoneNumber2: 6,
    mobilePhoneNumber2Name: 6,
    phoneNumber: 6,
    email: 6,
    birthday: 6,
    birthPlace: 6
  };

  constructor(
    private _translateService: TranslocoService,
    private _datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    for (const field of Object.keys(this.conflictingFields) as (keyof ContactModel)[]) {
      this.dropdownOptions[field] = [
        { label: this._translateService.translate('contacts.mergeNothing'), value: '__NONE__' },
        ...this.conflictingFields[field]!.map(val => ({
          label: field === 'gender'
            ? this._translateService.translate(`salutation.${val}`)
            : field === 'birthday'
              ? this.formatBirthday(val)
              : val,
          value: val
        }))
      ];
      this.mergedContact[field] = undefined;
      this.touchedFields.add(field);
    }
  }

  formatBirthday(value: any): string {
    return this._datePipe.transform(value, 'dd.MM.yyyy') ?? value;
  }
  onInputChange(field: keyof ContactModel): void {
    this.touchedFields.add(field);
  }

  isFieldInvalid(field: keyof ContactModel): boolean | undefined {
    return (
      this.conflictingFields[field] &&
      this.touchedFields.has(field) &&
      this.mergedContact[field] === undefined
    );
  }

  hasInvalidFields(): boolean {
    return this.allFields.some(f => this.isFieldInvalid(f));
  }

  confirm(): void {
    const invalids = this.allFields.filter(f => this.isFieldInvalid(f));
    if (invalids.length) {
      invalids.forEach(f => this.touchedFields.add(f));
      return;
    }

    const cleaned: Record<string, any> = {};
    for (const key of Object.keys(this.mergedContact)) {
      const value = this.mergedContact[key as keyof ContactModel];
      cleaned[key] = value === '__NONE__' ? null : value;
    }

    this.merged.emit(cleaned);
  }

  cancel(): void {
    this.canceled.emit();
  }
}
